/* eslint-disable*/
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import Swal from "sweetalert2";
import { CheckBox } from "../../components/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";
import { set } from "date-fns";

const SocialSharing = () => {
  const [formData, setFormData] = useState({
    message: "",
    imageUrl: null,
    checksharing: false,
  });
  const [socialPosts, setSocialPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;
  const MAX_MESSAGE_LENGTH = 1024;
  const MAX_IMAGE_SIZE_MB = 5;
  const MAX_IMAGE_DIMENSION_SUM = 10000;
  const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
  const [imageError, setImageError] = useState("");

  // Fetch social posts on component mount
  useEffect(() => {
    const fetchSocialPosts = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_URL}socialsharing`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSocialPosts(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching social posts:", err);
        showToast.error("Failed to load social posts");
        setLoading(false);
      }
    };

    fetchSocialPosts();
  }, []);

  // 📑 Update pagination when posts change
  useEffect(() => {
    setTotal(socialPosts.length);
    setPageCount(Math.ceil(socialPosts.length / limit));
    setPage(1);
  }, [socialPosts]);

  useEffect(() => {
    const handleClearEvent = () => {
      setImageError("");
      setFormData({
        message: "",
        imageUrl: null,
        checksharing: false,
      });
    };

    window.addEventListener('clearSocialSharingFiltersEvent', handleClearEvent);
    return () => {
      window.removeEventListener('clearSocialSharingFiltersEvent', handleClearEvent);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const getImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Invalid image"));
      };
      img.src = objectUrl;
    });

  const validateImage = async (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return "Only JPG, PNG and WEBP images are allowed.";
    }
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      return `Image size must not exceed ${MAX_IMAGE_SIZE_MB} MB.`;
    }
    try {
      const { width, height } = await getImageDimensions(file);
      if (width + height > MAX_IMAGE_DIMENSION_SUM) {
        return `Image width + height must not exceed ${MAX_IMAGE_DIMENSION_SUM} px (selected image is ${width} × ${height} px).`;
      }
    } catch (err) {
      return "The selected file is not a valid image.";
    }
    return "";
  };

  const handleFileChange = async (e) => {
    const input = e.target;
    const file = input.files[0];

    if (!file) {
      setImageError("");
      setFormData((prev) => ({ ...prev, imageUrl: null }));
      return;
    }

    const error = await validateImage(file);
    if (error) {
      input.value = "";
      setImageError(error);
      setFormData((prev) => ({ ...prev, imageUrl: null }));
      return;
    }

    setImageError("");
    setFormData((prev) => ({ ...prev, imageUrl: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const formDataToSend = new FormData();

      formDataToSend.append("message", formData.message);
      if (formData.imageUrl) {
        formDataToSend.append("imageUrl", formData.imageUrl);
      }
      // ✅ send number instead of boolean
      formDataToSend.append("checksharing", formData.checksharing ? 1 : 0);

      await axios.post(`${API_URL}socialsharing/save`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      // 🧹 Clear form
      setImageError("");
      setFormData({
        message: "",
        imageUrl: null,
        checksharing: false,
      });
      showToast.success("Post shared successfully!");
      // 🔄 refresh page after success
      // setTimeout(() => {
      //   window.location.reload();
      // }, 1000); // wait 1s so user can see toast
    } catch (err) {
      console.error("Error sharing post:", err);
      showToast.error(err.response?.data?.message || "Failed to share post");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this post!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");
          await axios.delete(`${API_URL}campaign/social-posts/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          showToast.success("Post deleted successfully!");
          setSocialPosts(socialPosts.filter((post) => post.id !== id));
        } catch (err) {
          console.error("Error deleting post:", err);
          showToast.error("Failed to delete post");
        }
      }
      setDeletingId(null);
    });
  };

  // 📑 Pagination calculation
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedPosts = socialPosts.slice(start - 1, end);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        <div className="panel panel-default with-rounded-corner with-shadow">
          <form
            onSubmit={handleSubmit}
            className="stdform"
            encType="multipart/form-data"
          >
            <div className="panel-heading">
              <span className="panel-title">Manage Social Sharing</span>
            </div>
            <div className="panel-body">
              <div className="row">
                <div className="col-md-12">
                  <div className="form-group">
                    <label className="control-label">Post Message</label>
                    <textarea
                      name="message"
                      className="form-control"
                      placeholder="Enter Message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="5"
                      maxLength={MAX_MESSAGE_LENGTH}
                      required
                    />
                    <div
                      className="text-right"
                      style={{
                        fontSize: "12px",
                        marginTop: "4px",
                        color:
                          formData.message.length >= MAX_MESSAGE_LENGTH
                            ? "#d9534f"
                            : "#777",
                      }}
                    >
                      {formData.message.length} / {MAX_MESSAGE_LENGTH} characters
                    </div>
                  </div>
                </div>

                <div className="col-md-12">
                  <div className="form-group">
                    <label className="control-label">Upload Image</label>
                    <input
                      type="file"
                      name="imageUrl"
                      className="form-control"
                      onChange={handleFileChange}
                      accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    />
                    <div
                      style={{
                        fontSize: "12px",
                        marginTop: "4px",
                        color: imageError ? "#d9534f" : "#777",
                      }}
                    >
                      {imageError || `Max allowed size: ${MAX_IMAGE_SIZE_MB} MB`}
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <CheckBox
                    label="Send Push Notification"
                    fieldname="checksharing"
                    id="global"
                    checked={formData.checksharing}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="clearfix"></div>
                <div className="col-md-3">
                  <div style={{ float: "left", marginTop: "20px" }}>
                    <PermissionWrapper module="socialsharing" action="add">
                      <button
                        type="submit"
                        name="submit"
                        className="btn btn-primary"
                      >
                        Submit
                      </button>
                    </PermissionWrapper>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
            <h4 className="panel-title mb-0">Social Posts</h4>

            {/* 📑 Pagination Controls */}
            {socialPosts.length > 0 && (
              <div className="d-inline-block ms-3">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>

          <div className="table-responsive" style={{ overflowX: "auto" }}>
            <table
              className="table table-condensed table-bordered bg-white"
              id="datatable"
            >
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>#</th>
                  <th>Post Message</th>
                  <th>Post Image</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPosts.length > 0 ? (
                  paginatedPosts.map((post, index) => (
                    <tr key={post.id || index}>
                      <td
                        style={{ textAlign: "center", verticalAlign: "middle" }}
                      >
                        {start + index}
                      </td>
                      <td
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {post.message}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {post.imageUrl ? (
                          <a
                            href={post.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-link"
                          >
                            Image
                          </a>
                        ) : (
                          <span className="text-muted">No image</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No social posts found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Scroll to top button */}
        <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
          <i className="ti-arrow-up"></i>
        </a>
      </div>
    </div>
  );
};

export default SocialSharing;
